import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

export default function CheckoutResult() {
  const [sp] = useSearchParams();
  const [tx, setTx] = useState(null);
  const [state, setState] = useState("polling");
  const sessionId = sp.get("session_id");
  const { clearCart } = useApp();

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setTx(data);
        if (data.payment_status === "paid") {
          setState("paid");
          if (data.type === "cart") clearCart();
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
      } catch {}
      attempts += 1;
      if (attempts >= 8) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId, clearCart]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-16" data-testid="checkout-result-page">
      <div className="card-soft p-10 text-center">
        {state === "polling" && (
          <>
            <Loader2 className="h-12 w-12 text-pink-500 mx-auto animate-spin" />
            <h1 className="font-display text-2xl font-bold mt-4">Confirming payment...</h1>
            <p className="text-slate-600 mt-2">Please wait while we verify your transaction.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" />
            <h1 data-testid="payment-success" className="font-display text-3xl font-bold mt-4 text-emerald-700">Thank you!</h1>
            <p className="text-slate-700 mt-2">Your payment of <strong>${tx?.amount?.toFixed(2)}</strong> was successful.</p>
            <p className="text-slate-500 text-sm mt-1">{tx?.metadata?.label || tx?.metadata?.items}</p>
            <div className="flex gap-3 justify-center mt-6">
              <Link to="/" className="btn-outline-pink">Home</Link>
              <Link to="/dashboard" className="btn-pink">View Dashboard</Link>
            </div>
          </>
        )}
        {(state === "timeout" || state === "expired" || state === "error") && (
          <>
            <AlertCircle className="h-12 w-12 text-amber-500 mx-auto" />
            <h1 className="font-display text-2xl font-bold mt-4">Payment status unclear</h1>
            <p className="text-slate-600 mt-2">If you completed payment, you'll receive an email confirmation shortly.</p>
            <Link to="/" className="btn-pink inline-block mt-5">Back home</Link>
          </>
        )}
      </div>
    </div>
  );
}
